import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Coins, MessageSquare } from "lucide-react";
import { api, setSession } from "../lib/api";
import { fmtAxvTrim } from "../lib/tokens";
import { IMAGES } from "../lib/tokens";
import Plate from "../components/Plate";
import { useWorld } from "../state/World";

const AXV = 1_000_000;
const NUDGE_STEPS = [0, 0.25, 1, 5];

/**
 * A PlayCard, shown to an invited Nudger.
 *
 * No Atlas. No World. Just the Challenge. A Nudger who backs it gets a real
 * Pouch on the real ledger — they are a full Endeavorum user from their first
 * click and simply have not met the World yet. When PlayForward eventually
 * tickets them, they discover the account they already had; there is nothing to
 * migrate, because it was one substrate the whole time.
 */
export default function CardPage() {
  const { cardId } = useParams();
  const [params] = useSearchParams();
  const invite = params.get("invite");

  const [view, setView] = useState(null);
  const [me, setMe] = useState(null);
  const [text, setText] = useState("");
  const [amount, setAmount] = useState(0.25);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const { cross, refresh } = useWorld();
  const nav = useNavigate();

  // The moment a Nudger finds out. They do not sign up — they walk into an
  // account that has existed since they opened the invitation. Whoever invited
  // them gets paid for the acquisition at exactly this point.
  const claimWorld = async () => {
    setClaiming(true);
    try {
      await api.post("/world/arrive");
      // The session was minted by /card/:id/accept, but WorldProvider still
      // holds the null it got before any session existed. Without this the
      // graduated Nudger lands on the Threshold — and the whole "you already
      // had an account" moment falls flat at the exact instant it should land.
      await refresh();
      const { external } = await cross("endeavorum", "/");
      if (!external) nav("/");
    } catch (e) {
      setErr("Could not open the World. Try again.");
      setClaiming(false);
    }
  };

  useEffect(() => {
    const boot = async () => {
      try {
        if (invite) {
          const { data } = await api.post(`/card/${cardId}/accept`, { invite });
          setSession(data.token);
          setView(data);
          const mine = await api.get("/me");
          setMe(mine.data);
          // The token has served its purpose; don't leave it in history.
          window.history.replaceState(null, "", window.location.pathname);
        } else {
          const { data } = await api.get(`/card/${cardId}`);
          setView(data);
          try {
            const mine = await api.get("/me");
            setMe(mine.data);
          } catch (e) {
            setMe(null);
          }
        }
      } catch (e) {
        setErr(e?.response?.data?.detail || "This Challenge could not be opened.");
      }
    };
    boot();
  }, [cardId, invite]);

  const send = async () => {
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post(`/card/${cardId}/nudge`, {
        text,
        amount_sub: Math.round(amount * AXV),
      });
      setView(data);
      setText("");
      setDone(true);
      const mine = await api.get("/me");
      setMe(mine.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "That Nudge did not land.");
    } finally {
      setBusy(false);
    }
  };

  if (err && !view) {
    return (
      <div className="grid min-h-screen place-items-center bg-[var(--ink)] px-6">
        <p data-testid="card-error" className="serif text-center text-lg text-white/60">
          {err}
        </p>
      </div>
    );
  }

  if (!view) {
    return (
      <div className="grid min-h-screen place-items-center bg-[var(--ink)]">
        <span className="mono text-[10px] tracking-[0.3em] text-white/25">OPENING…</span>
      </div>
    );
  }

  const card = view.playcard;
  const canNudge = Boolean(me) && (text.trim() || amount > 0);
  const deadline = card.deadline ? new Date(card.deadline) : null;
  const daysLeft = deadline
    ? Math.max(0, Math.ceil((deadline - new Date()) / 86400000))
    : null;

  return (
    <div className="relative min-h-screen bg-[var(--ink)]" data-testid="card-page">
      <Plate src={IMAGES.pfLounge} alt="" />

      <div className="relative z-10 mx-auto flex min-h-screen max-w-3xl flex-col justify-center px-6 py-20">
        {/* The card, as an object on the table. No Atlas, no walk-in — a Nudger
            is shown one thing and asked one question. */}
        <motion.div
          initial={{ opacity: 0, y: 34, rotateX: -12, rotateZ: -1.5 }}
          animate={{ opacity: 1, y: 0, rotateX: 0, rotateZ: -1.1 }}
          transition={{ duration: 1.1, ease: [0.2, 0.8, 0.2, 1] }}
          className="glass-warm relative overflow-hidden rounded-2xl p-7 md:p-10"
          data-testid="nudger-card"
          style={{ boxShadow: "0 40px 90px -30px rgba(0,0,0,0.9)" }}
        >
          <div
            className="absolute right-0 top-0 h-40 w-40 opacity-30"
            aria-hidden
            style={{
              background:
                "radial-gradient(circle at top right, var(--stage-gold), transparent 70%)",
            }}
          />
          <div className="relative">
            <div className="tracked mb-6 flex items-center justify-between gap-3 text-[9px]">
              <span className="text-[var(--stage-gold)]/80">
                {view.owner_name} asked you to look at this
              </span>
              {daysLeft !== null && (
                <span className="mono shrink-0 text-[var(--arcade-cyan)]" data-testid="card-days-left">
                  {daysLeft === 0 ? "due today" : `${daysLeft} days left`}
                </span>
              )}
            </div>

            {card.aim && card.slice && card.aim !== card.slice && (
              <div className="mb-2 flex items-baseline gap-2">
                <span className="mono shrink-0 text-[8px] uppercase text-white/30">Horizon</span>
                <span className="text-[12.5px] leading-snug text-white/45" data-testid="card-horizon">
                  {card.aim}
                </span>
              </div>
            )}

            <div className="mono mb-2 text-[8px] uppercase text-[var(--stage-gold)]/70">
              The Slice
            </div>
            <h1
              className="serif text-3xl leading-[1.1] text-white/95 sm:text-4xl"
              data-testid="card-slice"
            >
              {card.slice || card.aim}
            </h1>

            <dl className="mt-8 grid gap-6 border-t border-white/8 pt-6 sm:grid-cols-3" data-testid="card-detail">
              {[
                ["Lands", card.where || card.that],
                ["Cadence", card.cadence_name],
                ["Deadline", deadline ? deadline.toLocaleDateString() : card.when],
              ]
                .filter(([, v]) => v)
                .map(([k, v]) => (
                  <div key={k}>
                    <dt className="tracked mb-1.5 text-[9px] text-white/35">{k}</dt>
                    <dd className="text-sm leading-relaxed text-white/75">{v}</dd>
                  </div>
                ))}
            </dl>

            <div className="mt-8 flex flex-wrap items-center gap-6 border-t border-white/10 pt-6">
              <div>
                <div className="tracked mb-1 text-[9px] text-white/35">They staked</div>
                <div data-testid="card-ante" className="mono text-lg text-[var(--stage-gold)]">
                  {fmtAxvTrim(card.ante_sub)}
                </div>
              </div>
              <div>
                <div className="tracked mb-1 text-[9px] text-white/35">Nudgers behind it</div>
                <div data-testid="card-backed" className="mono text-lg text-[var(--arcade-cyan)]">
                  {fmtAxvTrim(view.backed_sub)}
                </div>
              </div>
              {me && (
                <div className="ml-auto">
                  <div className="tracked mb-1 text-[9px] text-white/35">Your Pouch</div>
                  <div data-testid="card-pouch" className="mono text-lg text-white/80">
                    {fmtAxvTrim(me.pouch_sub)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {me && (
          <motion.div
            className="glass mt-10 rounded-3xl p-6"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            data-testid="nudge-form"
          >
            <label className="tracked mb-3 block text-[9px] text-white/40">
              Say the useful thing
            </label>
            <textarea
              data-testid="nudge-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              placeholder="The bit they are avoiding is…"
              className="w-full resize-none rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white/85 outline-none transition-colors placeholder:text-white/20 focus:border-[var(--stage-gold)]/50"
            />

            <div className="mt-6">
              <div className="tracked mb-3 text-[9px] text-white/40">
                Put Tokens behind it
              </div>
              <div className="flex flex-wrap gap-2">
                {NUDGE_STEPS.map((v) => (
                  <button
                    key={v}
                    type="button"
                    data-testid={`nudge-amount-${v}`}
                    onClick={() => setAmount(v)}
                    className={`mono rounded-full border px-4 py-2 text-[11px] transition-colors ${
                      amount === v
                        ? "border-[var(--stage-gold)] text-[var(--stage-gold)]"
                        : "border-white/12 text-white/45 hover:border-white/30 hover:text-white/70"
                    }`}
                  >
                    {v === 0 ? "words only" : `${v} AXV`}
                  </button>
                ))}
              </div>
            </div>

            {err && (
              <p data-testid="nudge-error" className="mono mt-4 text-[11px] text-[var(--stage-ember)]">
                {err}
              </p>
            )}

            <button
              type="button"
              data-testid="nudge-submit"
              disabled={busy || !canNudge}
              onClick={send}
              className="group mt-7 flex w-full items-center justify-center gap-2.5 rounded-full bg-[var(--stage-gold)] px-6 py-4 text-[11px] uppercase tracking-[0.22em] text-[#1a1206] transition-all duration-200 hover:brightness-110 disabled:opacity-30"
            >
              {busy ? "Landing…" : amount > 0 ? `Nudge with ${amount} AXV` : "Nudge"}
              <ArrowRight
                size={14}
                strokeWidth={2}
                className="transition-transform duration-200 group-hover:translate-x-0.5"
              />
            </button>

            {amount > 0 && (
              <p className="mono mt-3 text-center text-[10px] text-white/30">
                Flat fee of 0.002 AXV applies. It will show on the Ledger.
              </p>
            )}

            {done && (
              <motion.div
                data-testid="nudge-done"
                className="mt-6 border-t border-white/10 pt-5"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <p className="mono text-[10px] leading-relaxed text-[var(--arcade-cyan)]">
                  That landed. It came out of your Pouch — which you have had
                  since you opened this link.
                </p>
                <p className="mt-3 text-[13px] leading-relaxed text-white/60">
                  You did not sign up for anything. There is a World behind this
                  Challenge, and an account in it that is already yours.
                </p>
                <button
                  type="button"
                  data-testid="claim-world"
                  disabled={claiming}
                  onClick={claimWorld}
                  className="group mt-5 flex w-full items-center justify-center gap-2.5 rounded-full border border-[var(--stage-gold)]/50 px-6 py-3.5 text-[10px] uppercase tracking-[0.22em] text-[var(--stage-gold)] transition-colors duration-200 hover:bg-[var(--stage-gold)]/10 disabled:opacity-40"
                >
                  {claiming ? "Opening…" : "See what you already have"}
                  <ArrowRight
                    size={13}
                    strokeWidth={2}
                    className="transition-transform duration-200 group-hover:translate-x-0.5"
                  />
                </button>
              </motion.div>
            )}
          </motion.div>
        )}

        {view.nudges.length > 0 && (
          <div className="mt-12" data-testid="nudge-list">
            <div className="tracked mb-5 text-[9px] text-white/35">
              {view.nudges.length} {view.nudges.length === 1 ? "Nudge" : "Nudges"}
            </div>
            <ul className="space-y-5">
              {view.nudges.map((n) => (
                <li key={n.id} data-testid="nudge-item" className="border-l border-white/10 pl-5">
                  <div className="mono mb-1.5 flex items-center gap-3 text-[9px] text-white/30">
                    <span className="text-white/55">{n.display_name}</span>
                    {n.amount_sub > 0 && (
                      <span className="flex items-center gap-1 text-[var(--stage-gold)]">
                        <Coins size={10} strokeWidth={1.8} />
                        {fmtAxvTrim(n.amount_sub)}
                      </span>
                    )}
                  </div>
                  {n.text && (
                    <p className="text-[13px] leading-relaxed text-white/70">{n.text}</p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {!me && (
          <div className="glass mt-10 flex items-center gap-3 rounded-3xl px-6 py-5">
            <MessageSquare size={15} strokeWidth={1.6} className="shrink-0 text-white/35" />
            <p className="text-[13px] leading-relaxed text-white/60">
              Open the invitation you were sent to Nudge this Challenge.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
